
import React, { useState, useEffect, useCallback } from "react";
import { User } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Users, Briefcase, UserCircle, Save, X, Plus, Edit, Trash2, DollarSign, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { getAvatarColor } from "@/lib/utils";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";


const modules = [
  {
    id: "leads",
    name: "Leads",
    icon: Users,
    color: "text-purple-600",
    bgColor: "bg-purple-100",
    description: "Configure lead management settings"
  },
  {
    id: "deals",
    name: "Deals",
    icon: DollarSign,
    color: "text-green-600",
    bgColor: "bg-green-100",
    description: "Configure sales pipeline and deal management"
  },
  {
    id: "contacts", // Changed from "customers"
    name: "Contacts", // Changed from "Customers"
    icon: UserCircle,
    color: "text-blue-600",
    bgColor: "bg-blue-100",
    description: "Configure contact management settings" // Changed from "customer"
  },
  {
    id: "cases",
    name: "Cases",
    icon: Briefcase,
    color: "text-emerald-600",
    bgColor: "bg-emerald-100",
    description: "Configure case management settings"
  },
  {
    id: "services",
    name: "Services",
    icon: Briefcase,
    color: "text-indigo-600",
    bgColor: "bg-indigo-100",
    description: "Configure service packages and pricing"
  }
];

const defaultLeadStatuses = ["new", "contacted", "qualifying", "converted", "lost"];
const defaultContactStatuses = ["active", "inactive", "completed"]; // Changed from defaultCustomerStatuses

const defaultCaseTypes = [
  { id: "family_based", name: "Family-Based Immigration", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "employment_based", name: "Employment-Based Immigration", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "asylum", name: "Asylum", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "naturalization", name: "Naturalization", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "visa", name: "Visa Application", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "deportation_defense", name: "Deportation Defense", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "adjustment_of_status", name: "Adjustment of Status", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "other", name: "Other", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 }
];
// Valid case_status enum values from DB
const defaultCaseStatuses = ["preparation", "document_collection", "petition_filed", "rfe_response", "biometrics", "interview", "adjudication", "approved", "denied", "withdrawn", "closed"];
const defaultRequiredLeadFields = ["first_name", "last_name", "phone"];
const availableLeadFields = [
  "first_name", "last_name", "email", "phone", "company",
  "date_of_birth", "nationality", "preferred_language", "address",
  "source", "status", "assigned_to", "notes"
];

const defaultDealFields = ["deal_name", "contact_id", "deal_type", "stage"];
const dealFields = [
  { id: "deal_name", label: "Deal Name" },
  { id: "contact_id", label: "Contact" },
  { id: "lead_id", label: "Lead" },
  { id: "deal_type", label: "Deal Type (Case/Service/Both)" },
  { id: "case_type", label: "Case Type" },
  { id: "case_price", label: "Case Price" },
  { id: "service_type", label: "Service Type" },
  { id: "service_price", label: "Service Price" },
  { id: "stage", label: "Stage" },
  { id: "probability", label: "Probability" },
  { id: "expected_close_date", label: "Expected Close Date" },
  { id: "assigned_to", label: "Assigned To" },
  { id: "source", label: "Source" },
  { id: "description", label: "Description" }
];

const defaultCasePersonLabels = {
  asylum: {
    primary_person_label: "Applicant",
    show_dependents: true,
    dependent_label: "Dependent",
    can_differ_from_customer: false,
    show_secondary_person: false,
    secondary_person_label: "",
    secondary_person_can_differ_from_customer: false
  },
  family_based: {
    primary_person_label: "Beneficiary",
    show_dependents: true,
    dependent_label: "Family Member",
    can_differ_from_customer: true,
    show_secondary_person: true,
    secondary_person_label: "Petitioner",
    secondary_person_can_differ_from_customer: false
  },
  employment_based: {
    primary_person_label: "Beneficiary",
    show_dependents: false,
    dependent_label: "",
    can_differ_from_customer: true,
    show_secondary_person: true,
    secondary_person_label: "Petitioner",
    secondary_person_can_differ_from_customer: false
  },
  naturalization: {
    primary_person_label: "Applicant",
    show_dependents: false,
    dependent_label: "",
    can_differ_from_customer: false,
    show_secondary_person: false,
    secondary_person_label: "",
    secondary_person_can_differ_from_customer: false
  },
  visa: {
    primary_person_label: "Applicant",
    show_dependents: false,
    dependent_label: "",
    can_differ_from_customer: true,
    show_secondary_person: false,
    secondary_person_label: "",
    secondary_person_can_differ_from_customer: false
  },
  deportation_defense: {
    primary_person_label: "Applicant",
    show_dependents: false,
    dependent_label: "",
    can_differ_from_customer: false,
    show_secondary_person: false,
    secondary_person_label: "",
    secondary_person_can_differ_from_customer: false
  },
  adjustment_of_status: {
    primary_person_label: "Applicant",
    show_dependents: true,
    dependent_label: "Dependent",
    can_differ_from_customer: false,
    show_secondary_person: false,
    secondary_person_label: "",
    secondary_person_can_differ_from_customer: false
  },
  other: {
    primary_person_label: "Applicant",
    show_dependents: false,
    dependent_label: "",
    can_differ_from_customer: false,
    show_secondary_person: false,
    secondary_person_label: "",
    secondary_person_can_differ_from_customer: false
  }
};

const defaultServiceTypes = [
  { id: "consultation", name: "Consultation", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "family_based_package", name: "Family-Based Package", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "employment_based_package", name: "Employment-Based Package", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "asylum_package", name: "Asylum Package", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "naturalization_package", name: "Naturalization Package", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "visa_application", name: "Visa Application", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "document_preparation", name: "Document Preparation", pricing_type: "variable", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "representation", name: "Representation", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "appeal", name: "Appeal", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 },
  { id: "other", name: "Other", pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 }
];
const defaultServiceStatuses = ["pending", "active", "completed", "cancelled", "on_hold"];


export default function ModuleSettings() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [editingStatus, setEditingStatus] = useState(null);
  const [newContactStatus, setNewContactStatus] = useState(""); // Changed from newCustomerStatus
  const [editingContactStatus, setEditingContactStatus] = useState(null); // Changed from editingCustomerStatus

  const [newCaseType, setNewCaseType] = useState({
    id: "",
    name: "",
    pricing_type: "flat",
    default_price: "",
    has_cost: false,
    default_cost: ""
  });
  const [editingCaseType, setEditingCaseType] = useState(null);
  const [caseTypeModalOpen, setCaseTypeModalOpen] = useState(false);
  const [newCaseTypeLabels, setNewCaseTypeLabels] = useState({
    primary_person_label: "",
    show_dependents: false,
    dependent_label: "",
    can_differ_from_customer: false,
    show_secondary_person: false,
    secondary_person_label: "",
    secondary_person_can_differ_from_customer: false
  });

  const [newCaseStatus, setNewCaseStatus] = useState("");
  const [editingCaseStatus, setEditingCaseStatus] = useState(null);

  const [selectedCaseTypeForMilestones, setSelectedCaseTypeForMilestones] = useState("");
  const [newMilestone, setNewMilestone] = useState({ name: "", days_from_start: "" });
  const [editingMilestoneIndex, setEditingMilestoneIndex] = useState(null);

  const [newServiceType, setNewServiceType] = useState({
    id: "",
    name: "",
    pricing_type: "flat",
    default_price: "",
    has_cost: false,
    default_cost: ""
  });
  const [editingServiceType, setEditingServiceType] = useState(null); // Will hold the ID of the service type being edited
  const [showAddServiceType, setShowAddServiceType] = useState(false);
  const [newServiceStatus, setNewServiceStatus] = useState("");
  const [editingServiceStatus, setEditingServiceStatus] = useState(null);

  const [selectedStatusForReasons, setSelectedStatusForReasons] = useState("");
  const [newReason, setNewReason] = useState("");

  const [leadSourcesConfig, setLeadSourcesConfig] = useState([]);
  const [newLeadSource, setNewLeadSource] = useState("");
  const [editingLeadSourceId, setEditingLeadSourceId] = useState(null);
  const [editingLeadSourceName, setEditingLeadSourceName] = useState("");

  // New state variables for lead assignment
  const [assignmentMethod, setAssignmentMethod] = useState("position");
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const availablePositions = [
    "Sales Consultant",
    "Sales Development",
    "Customer Service",
    "Customer Experience",
    "Case Analyst",
    "Case Manager",
    "Case Assistant"
  ];


  const [moduleSettings, setModuleSettings] = useState({
    leads: {
      enabled: true,
      required_fields: defaultRequiredLeadFields,
      visible_fields: ["first_name", "last_name", "email", "phone", "company", "source", "status", "assigned_to"],
      custom_statuses: defaultLeadStatuses,
      custom_lead_sources: [], // This will be initialized in loadData
      auto_assignment: false,
      assignment_method: "position", // New
      assignment_positions: [], // New
      assignment_users: [], // New
      last_assigned_index: 0 // New
    },
    deals: {
      enabled: true,
      required_fields: defaultDealFields,
      visible_fields: ["deal_name", "contact_id", "deal_type", "total_value", "stage", "assigned_to"],
      custom_stages: ["prospecting", "qualification", "proposal", "negotiation", "closed_won", "closed_lost"],
      auto_assignment: false
    },
    contacts: { // Changed from customers
      enabled: true,
      required_fields: ["first_name", "last_name", "email", "primary_phone"],
      visible_fields: ["first_name", "last_name", "email", "primary_phone", "primary_address", "status"],
      custom_fields: [],
      auto_create_from_lead: true,
      custom_statuses: defaultContactStatuses, // Added to initialize
      statuses_requiring_reason: [], // New: Array of status IDs that require a reason
      status_reasons: {} // New: Object mapping status ID to array of reasons
    },
    cases: {
      enabled: true,
      required_fields: ["contact_id", "case_type", "status"], // Changed from customer_id to contact_id
      visible_fields: ["contact_id", "case_number", "case_type", "status", "priority", "filing_date"], // Changed from customer_id to contact_id
      custom_case_types: defaultCaseTypes,
      custom_case_statuses: defaultCaseStatuses,
      milestone_templates: [],
      auto_notifications: true,
      case_person_labels: defaultCasePersonLabels
    },
    services: {
      enabled: true,
      required_fields: ["contact_id", "service_type", "price"],
      visible_fields: ["service_name", "service_type", "status", "price", "start_date"],
      custom_service_types: defaultServiceTypes,
      custom_service_statuses: defaultServiceStatuses,
      auto_notifications: false
    }
  });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const userData = await User.me();
      setCurrentUser(userData);

      if (userData.role !== 'admin') {
        navigate(createPageUrl("Settings"));
        return;
      }

      // Load all users for assignment and sort alphabetically
      const usersData = await User.list();
      const sortedUsers = usersData.sort((a, b) => {
        const nameA = (a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : a.full_name || a.email || '').toLowerCase();
        const nameB = (b.first_name && b.last_name ? `${b.first_name} ${b.last_name}` : b.full_name || b.email || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setAllUsers(sortedUsers);

      // Default lead sources
      const defaultLeadSources = [
        { id: "website", name: "Website" },
        { id: "referral", name: "Referral" },
        { id: "social_media", name: "Social Media" },
        { id: "email_campaign", name: "Email Campaign" },
        { id: "phone_call", name: "Phone Call" },
        { id: "walk_in", name: "Walk In" },
        { id: "other", name: "Other" }
      ];

      if (userData.module_settings) {
        setModuleSettings(prev => {
          const loadedLeadsSettings = userData.module_settings.leads || {};
          const leadsSettings = {
            ...prev.leads,
            ...loadedLeadsSettings,
            custom_statuses: loadedLeadsSettings.custom_statuses || defaultLeadStatuses,
            required_fields: loadedLeadsSettings.required_fields || defaultRequiredLeadFields,
            custom_lead_sources: loadedLeadsSettings.custom_lead_sources || defaultLeadSources,
            auto_assignment: loadedLeadsSettings.auto_assignment || false,
            assignment_method: loadedLeadsSettings.assignment_method || "position",
            assignment_positions: loadedLeadsSettings.assignment_positions || [],
            assignment_users: loadedLeadsSettings.assignment_users || [],
            last_assigned_index: loadedLeadsSettings.last_assigned_index || 0
          };

          // Set local state for assignment configuration
          setAssignmentMethod(leadsSettings.assignment_method);
          setSelectedPositions(leadsSettings.assignment_positions);
          setSelectedUsers(leadsSettings.assignment_users);

          const loadedDealsSettings = userData.module_settings.deals || {};
          const dealsSettings = {
            ...prev.deals,
            ...loadedDealsSettings,
            custom_stages: loadedDealsSettings.custom_stages || ["prospecting", "qualification", "proposal", "negotiation", "closed_won", "closed_lost"],
            required_fields: loadedDealsSettings.required_fields || defaultDealFields
          };

          const loadedContactsSettings = userData.module_settings.contacts || {}; // Changed from loadedCustomersSettings
          const contactsSettings = { // Changed from customersSettings
            ...prev.contacts, // Changed from prev.customers
            ...loadedContactsSettings, // Changed from loadedCustomersSettings
            custom_statuses: loadedContactsSettings.custom_statuses || defaultContactStatuses, // Changed from defaultCustomerStatuses
            statuses_requiring_reason: loadedContactsSettings.statuses_requiring_reason || [],
            status_reasons: loadedContactsSettings.status_reasons || {}
          };

          const loadedCasesSettings = userData.module_settings.cases || {};
          let shouldMigrateAndSaveCaseTypes = false;

          // Convert old string-based case types to new object format
          const loadedCustomCaseTypes = loadedCasesSettings.custom_case_types || [];
          const finalCustomCaseTypesMap = new Map();

          // 1. Add all default case types to the map
          defaultCaseTypes.forEach(type => {
            finalCustomCaseTypesMap.set(type.id, { ...type });
          });

          // 2. Iterate through loaded custom case types
          loadedCustomCaseTypes.forEach(loadedType => {
            if (typeof loadedType === 'string') {
              shouldMigrateAndSaveCaseTypes = true; // Mark for migration save
              // Handle old string-based custom types
              const id = loadedType.toLowerCase().replace(/\s+/g, '_');
              if (!finalCustomCaseTypesMap.has(id)) {
                finalCustomCaseTypesMap.set(id, {
                  id: id,
                  name: loadedType.replace(/_/g, ' '),
                  pricing_type: "flat",
                  default_price: 0,
                  has_cost: false,
                  default_cost: 0
                });
              }
            } else if (typeof loadedType === 'object' && loadedType !== null && loadedType.id) {
              // Handle new object-based custom types
              const existingDefault = finalCustomCaseTypesMap.get(loadedType.id);
              finalCustomCaseTypesMap.set(loadedType.id, {
                ...(existingDefault || { // Use existing default as base, or a minimal base if unknown ID
                  id: loadedType.id,
                  pricing_type: "flat",
                  default_price: 0,
                  has_cost: false,
                  default_cost: 0
                }),
                ...loadedType, // Overlay with loaded values
                name: loadedType.name || (existingDefault ? existingDefault.name : loadedType.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
              });
            }
          });

          const finalCaseTypesArray = Array.from(finalCustomCaseTypesMap.values());

          // Trigger saving if migration is needed
          if (shouldMigrateAndSaveCaseTypes) {
            console.log("=== MIGRATING CASE TYPES FROM STRINGS TO OBJECTS ===");
            console.log("Migrated case types:", finalCaseTypesArray);

            setTimeout(async () => {
              try {
                // We need to fetch the latest userData again to ensure we don't overwrite concurrent changes
                const latestUserData = await User.me();
                await User.updateMyUserData({
                  module_settings: {
                    ...latestUserData.module_settings, // Start with latest state
                    cases: {
                      ...latestUserData.module_settings.cases, // Preserve other case settings
                      custom_case_types: finalCaseTypesArray // Save the newly formatted array
                    }
                  }
                });
                console.log("Case types migration saved successfully");
              } catch (error) {
                console.error("Error saving migrated case types:", error);
              }
            }, 100); // Small delay to not block initial render
          }

          const casesSettings = {
            ...prev.cases,
            ...loadedCasesSettings,
            custom_case_types: finalCaseTypesArray, // Use the processed array here
            custom_case_statuses: loadedCasesSettings.custom_case_statuses || defaultCaseStatuses,
            milestone_templates: loadedCasesSettings.milestone_templates || [],
            case_person_labels: (() => {
              const mergedLabels = {};
              const loadedLabels = loadedCasesSettings.case_person_labels || {};

              for (const typeId in defaultCasePersonLabels) {
                mergedLabels[typeId] = {
                  ...defaultCasePersonLabels[typeId],
                  ...(loadedLabels[typeId] || {})
                };
              }

              for (const typeId in loadedLabels) {
                  if (!mergedLabels[typeId]) {
                      mergedLabels[typeId] = {
                          ...defaultCasePersonLabels.other,
                          ...loadedLabels[typeId]
                      };
                  }
              }
              return mergedLabels;
            })()
          };

          const loadedServicesSettings = userData.module_settings.services || {};
          const loadedCustomServiceTypes = loadedServicesSettings.custom_service_types || [];

          const finalCustomServiceTypes = new Map();

          // 1. Add all default service types to the map with their full structure
          defaultServiceTypes.forEach(type => {
            finalCustomServiceTypes.set(type.id, { ...type });
          });

          // 2. Iterate through loaded custom service types
          loadedCustomServiceTypes.forEach(loadedType => {
            if (typeof loadedType === 'string') {
              // Handle old string-based custom types (e.g., ["consultation", "new_type"])
              const id = loadedType.toLowerCase().replace(/\s+/g, '_');
              if (!finalCustomServiceTypes.has(id)) {
                // If it's a new custom type (not in defaults), add it with base properties
                finalCustomServiceTypes.set(id, {
                  id: id,
                  name: loadedType.replace(/_/g, ' '),
                  pricing_type: "flat",
                  default_price: 0,
                  has_cost: false,
                  default_cost: 0
                });
              }
            } else if (typeof loadedType === 'object' && loadedType !== null && loadedType.id) {
              // Handle new object-based custom types
              const existingDefault = finalCustomServiceTypes.get(loadedType.id);
              finalCustomServiceTypes.set(loadedType.id, {
                ...(existingDefault || { // Use existing default as base, or a minimal base if unknown ID
                  id: loadedType.id,
                  pricing_type: "flat",
                  default_price: 0,
                  has_cost: false,
                  default_cost: 0
                }),
                ...loadedType, // Overlay with loaded values
                // Ensure name is set if not provided in loadedType but available in existingDefault or derived from id
                name: loadedType.name || (existingDefault ? existingDefault.name : loadedType.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())),
              });
            }
          });

          const servicesSettings = {
            ...prev.services,
            ...loadedServicesSettings,
            custom_service_types: Array.from(finalCustomServiceTypes.values()),
            custom_service_statuses: loadedServicesSettings.custom_service_statuses || defaultServiceStatuses
          };

          const newModuleSettings = {
            ...prev,
            ...userData.module_settings, // overlay with the entire loaded settings
            leads: leadsSettings,
            deals: dealsSettings,
            contacts: contactsSettings,
            cases: casesSettings,
            services: servicesSettings
          };
          setLeadSourcesConfig(newModuleSettings.leads.custom_lead_sources || defaultLeadSources);

          return newModuleSettings;
        });
      } else {
        // If userData.module_settings is completely empty, initialize with all defaults including lead sources
        setModuleSettings(prev => {
          const defaultModuleSettingsWithLeadSources = {
            ...prev,
            leads: {
              ...prev.leads,
              custom_lead_sources: defaultLeadSources,
              custom_statuses: defaultLeadStatuses,
              required_fields: defaultRequiredLeadFields,
              assignment_method: "position",
              assignment_positions: [],
              assignment_users: [],
              last_assigned_index: 0
            },
            deals: {
              ...prev.deals,
              custom_stages: ["prospecting", "qualification", "proposal", "negotiation", "closed_won", "closed_lost"],
              required_fields: defaultDealFields
            },
            contacts: {
              ...prev.contacts,
              custom_statuses: defaultContactStatuses,
              statuses_requiring_reason: [],
              status_reasons: {}
            },
            cases: {
              ...prev.cases,
              custom_case_types: defaultCaseTypes,
              custom_case_statuses: defaultCaseStatuses,
              milestone_templates: [],
              case_person_labels: defaultCasePersonLabels
            },
            services: {
              ...prev.services,
              custom_service_types: defaultServiceTypes,
              custom_service_statuses: defaultServiceStatuses
            }
          };
          setLeadSourcesConfig(defaultLeadSources); // Initialize separate state
          // Initialize assignment states
          setAssignmentMethod("position");
          setSelectedPositions([]);
          setSelectedUsers([]);
          return defaultModuleSettingsWithLeadSources;
        });
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setIsLoading(false);
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await User.updateMyUserData({ module_settings: moduleSettings });
      await loadData();
    } catch (error) {
      console.error("Error saving module settings:", error);
    }
    setIsSaving(false);
  };

  const handleModuleToggle = (moduleId, field, value) => {
    setModuleSettings(prev => ({
      ...prev,
      [moduleId]: {
        ...prev[moduleId],
        [field]: value
      }
    }));
  };

  const handleAddStatus = () => {
    if (!newStatus.trim()) return;

    const normalizedNewStatus = newStatus.trim().toLowerCase().replace(/\s+/g, '_');
    const currentStatuses = moduleSettings.leads.custom_statuses || [];

    if (!currentStatuses.includes(normalizedNewStatus)) {
      setModuleSettings(prev => ({
        ...prev,
        leads: {
          ...prev.leads,
          custom_statuses: [...currentStatuses, normalizedNewStatus]
        }
      }));
    }
    setNewStatus("");
  };

  const handleRemoveStatus = (statusToRemove) => {
    setModuleSettings(prev => ({
      ...prev,
      leads: {
        ...prev.leads,
        custom_statuses: prev.leads.custom_statuses.filter(s => s !== statusToRemove)
      }
    }));
  };

  const handleEditStatus = (oldStatus, newStatusValue) => {
    const trimmedNewStatusValue = newStatusValue.trim();
    if (!trimmedNewStatusValue) {
      handleRemoveStatus(oldStatus);
      setEditingStatus(null);
      return;
    }

    const normalizedNewStatus = trimmedNewStatusValue.toLowerCase().replace(/\s+/g, '_');
    const currentStatuses = moduleSettings.leads.custom_statuses || [];

    if (normalizedNewStatus === oldStatus || (currentStatuses.includes(normalizedNewStatus) && normalizedNewStatus !== oldStatus)) {
      setEditingStatus(null);
      return;
    }

    setModuleSettings(prev => ({
      ...prev,
      leads: {
        ...prev.leads,
        custom_statuses: prev.leads.custom_statuses.map(s => s === oldStatus ? normalizedNewStatus : s)
      }
    }));
    setEditingStatus(null);
  };

  const handleAddContactStatus = () => { // Changed from handleAddCustomerStatus
    if (!newContactStatus.trim()) return; // Changed from newCustomerStatus

    const normalizedNewStatus = newContactStatus.trim().toLowerCase().replace(/\s+/g, '_'); // Changed from newCustomerStatus
    const currentStatuses = moduleSettings.contacts.custom_statuses || []; // Changed from customers

    if (!currentStatuses.includes(normalizedNewStatus)) {
      setModuleSettings(prev => ({
        ...prev,
        contacts: { // Changed from customers
          ...prev.contacts, // Changed from customers
          custom_statuses: [...currentStatuses, normalizedNewStatus]
        }
      }));
    }
    setNewContactStatus(""); // Changed from setNewCustomerStatus
  };

  const handleRemoveContactStatus = (statusToRemove) => { // Changed from handleRemoveCustomerStatus
    setModuleSettings(prev => ({
      ...prev,
      contacts: { // Changed from customers
        ...prev.contacts, // Changed from customers
        custom_statuses: prev.contacts.custom_statuses.filter(s => s !== statusToRemove) // Changed from customers
      }
    }));
  };

  const handleEditContactStatus = (oldStatus, newStatusValue) => { // Changed from handleEditCustomerStatus
    const trimmedNewStatusValue = newStatusValue.trim();
    if (!trimmedNewStatusValue) {
      handleRemoveContactStatus(oldStatus); // Changed from handleRemoveCustomerStatus
      setEditingContactStatus(null); // Changed from setEditingCustomerStatus
      return;
    }

    const normalizedNewStatus = trimmedNewStatusValue.toLowerCase().replace(/\s+/g, '_');
    const currentStatuses = moduleSettings.contacts.custom_statuses || []; // Changed from customers

    if (normalizedNewStatus === oldStatus || (currentStatuses.includes(normalizedNewStatus) && normalizedNewStatus !== oldStatus)) {
      setEditingContactStatus(null); // Changed from setEditingCustomerStatus
      return;
    }

    setModuleSettings(prev => ({
      ...prev,
      contacts: { // Changed from customers
        ...prev.contacts, // Changed from customers
        custom_statuses: prev.contacts.custom_statuses.map(s => s === oldStatus ? normalizedNewStatus : s) // Changed from customers
      }
    }));
    setEditingContactStatus(null); // Changed from setEditingCustomerStatus
  };

  const handleSaveCaseType = () => {
    if (!newCaseType.name.trim() ||
        !newCaseTypeLabels.primary_person_label.trim() ||
        (newCaseTypeLabels.show_secondary_person && !newCaseTypeLabels.secondary_person_label.trim())) {
      return;
    }

    const normalizedCaseType = newCaseType.name.trim().toLowerCase().replace(/\s+/g, '_');

    const caseTypeToSave = {
      id: normalizedCaseType,
      name: newCaseType.name.trim(),
      pricing_type: newCaseType.pricing_type,
      default_price: Number(newCaseType.default_price) || 0,
      has_cost: newCaseType.has_cost,
      default_cost: newCaseType.has_cost ? Number(newCaseType.default_cost) || 0 : 0
    };

    setModuleSettings(prev => {
      const currentCaseTypes = prev.cases.custom_case_types || [];
      const currentCasePersonLabels = prev.cases.case_person_labels || {};
      const currentMilestoneTemplates = prev.cases.milestone_templates || [];

      let updatedCaseTypes = [...currentCaseTypes];
      let updatedCasePersonLabels = { ...currentCasePersonLabels };
      let updatedMilestoneTemplates = [...currentMilestoneTemplates];

      if (editingCaseType) {
        const oldId = typeof editingCaseType === 'string' ? editingCaseType : editingCaseType.id;
        const normalizedEditingCaseType = oldId.trim().toLowerCase().replace(/\s+/g, '_');

        if (normalizedEditingCaseType !== normalizedCaseType) {
          updatedCaseTypes = updatedCaseTypes.map(t => {
            const tId = typeof t === 'string' ? t : t.id;
            return tId === normalizedEditingCaseType ? caseTypeToSave : t;
          });

          if (updatedCasePersonLabels[normalizedEditingCaseType]) {
            updatedCasePersonLabels[normalizedCaseType] = updatedCasePersonLabels[normalizedEditingCaseType];
            delete updatedCasePersonLabels[normalizedEditingCaseType];
          }
          updatedMilestoneTemplates = updatedMilestoneTemplates.map(template => {
            if (template.case_type === normalizedEditingCaseType) {
              return { ...template, case_type: normalizedCaseType };
            }
            return template;
          });
        } else {
          updatedCaseTypes = updatedCaseTypes.map(t => {
            const tId = typeof t === 'string' ? t : t.id;
            return tId === normalizedEditingCaseType ? caseTypeToSave : t;
          });
        }
        updatedCasePersonLabels[normalizedCaseType] = { ...newCaseTypeLabels };

      } else {
        const exists = updatedCaseTypes.some(t => {
          const tId = typeof t === 'string' ? t : t.id;
          return tId === normalizedCaseType;
        });
        if (!exists) {
          updatedCaseTypes = [...updatedCaseTypes, caseTypeToSave];
        } else {
          updatedCaseTypes = updatedCaseTypes.map(t => {
            const tId = typeof t === 'string' ? t : t.id;
            return tId === normalizedCaseType ? caseTypeToSave : t;
          });
        }
        updatedCasePersonLabels[normalizedCaseType] = { ...newCaseTypeLabels };
      }

      return {
        ...prev,
        cases: {
          ...prev.cases,
          custom_case_types: updatedCaseTypes,
          case_person_labels: updatedCasePersonLabels,
          milestone_templates: updatedMilestoneTemplates
        }
      };
    });

    handleCancelCaseType();
  };

  const handleEditCaseType = (caseTypeToEdit) => {
    const caseTypeObj = typeof caseTypeToEdit === 'string'
      ? { id: caseTypeToEdit, name: caseTypeToEdit.replace(/_/g, ' '), pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 }
      : caseTypeToEdit;

    setNewCaseType({
      id: caseTypeObj.id,
      name: caseTypeObj.name,
      pricing_type: caseTypeObj.pricing_type || "flat",
      default_price: String(caseTypeObj.default_price || ""),
      has_cost: caseTypeObj.has_cost || false,
      default_cost: String(caseTypeObj.default_cost || "")
    });
    setEditingCaseType(caseTypeObj);
    const labels = moduleSettings.cases.case_person_labels?.[caseTypeObj.id] || defaultCasePersonLabels.other;
    setNewCaseTypeLabels({ ...labels });
    setCaseTypeModalOpen(true);
  };

  const handleDeleteCaseType = (typeToRemove) => {
    const typeId = typeof typeToRemove === 'string' ? typeToRemove : typeToRemove.id;
    setModuleSettings(prev => {
      const updatedCaseTypes = prev.cases.custom_case_types.filter(t => {
        const tId = typeof t === 'string' ? t : t.id;
        return tId !== typeId;
      });
      const updatedCasePersonLabels = { ...prev.cases.case_person_labels };
      delete updatedCasePersonLabels[typeId];

      const updatedMilestoneTemplates = (prev.cases.milestone_templates || []).filter(
        template => template.case_type === undefined || template.case_type !== typeId // handle undefined case_type as well
      );


      return {
        ...prev,
        cases: {
          ...prev.cases,
          custom_case_types: updatedCaseTypes,
          case_person_labels: updatedCasePersonLabels,
          milestone_templates: updatedMilestoneTemplates
        }
      };
    });
    if (selectedCaseTypeForMilestones === typeId) {
      setSelectedCaseTypeForMilestones("");
    }
  };

  const handleCancelCaseType = () => {
    setNewCaseType({
      id: "",
      name: "",
      pricing_type: "flat",
      default_price: "",
      has_cost: false,
      default_cost: ""
    });
    setEditingCaseType(null);
    setNewCaseTypeLabels({
      primary_person_label: "",
      show_dependents: false,
      dependent_label: "",
      can_differ_from_customer: false,
      show_secondary_person: false,
      secondary_person_label: "",
      secondary_person_can_differ_from_customer: false
    });
    setCaseTypeModalOpen(false);
  };

  const handleAddCaseStatus = () => {
    if (!newCaseStatus.trim()) return;

    const normalizedNewStatus = newCaseStatus.trim().toLowerCase().replace(/\s+/g, '_');
    const currentStatuses = moduleSettings.cases.custom_case_statuses || [];

    if (!currentStatuses.includes(normalizedNewStatus)) {
      setModuleSettings(prev => ({
        ...prev,
        cases: {
          ...prev.cases,
          custom_case_statuses: [...currentStatuses, normalizedNewStatus]
        }
      }));
    }
    setNewCaseStatus("");
  };

  const handleRemoveCaseStatus = (statusToRemove) => {
    setModuleSettings(prev => ({
      ...prev,
      cases: {
        ...prev.cases,
        custom_case_statuses: prev.cases.custom_case_statuses.filter(s => s !== statusToRemove)
      }
    }));
  };

  const handleEditCaseStatus = (oldStatus, newStatusValue) => {
    const trimmedNewStatusValue = newStatusValue.trim();
    if (!trimmedNewStatusValue) {
      handleRemoveCaseStatus(oldStatus);
      setEditingCaseStatus(null);
      return;
    }

    const normalizedNewStatus = trimmedNewStatusValue.toLowerCase().replace(/\s+/g, '_');
    const currentStatuses = moduleSettings.cases.custom_case_statuses || [];

    if (normalizedNewStatus === oldStatus || (currentStatuses.includes(normalizedNewStatus) && normalizedNewStatus !== oldStatus)) {
      setEditingCaseStatus(null);
      return;
    }

    setModuleSettings(prev => ({
      ...prev,
      cases: {
        ...prev.cases,
        custom_case_statuses: prev.cases.custom_case_statuses.map(s => s === oldStatus ? normalizedNewStatus : s)
      }
    }));
    setEditingCaseStatus(null);
  };

  const handleToggleRequiredField = (field) => {
    setModuleSettings(prev => ({
      ...prev,
      leads: {
        ...prev.leads,
        required_fields: prev.leads.required_fields.includes(field)
          ? prev.leads.required_fields.filter(f => f !== field)
          : [...prev.leads.required_fields, field]
      }
    }));
  };

  const handleAddMilestoneTemplate = () => {
    if (!selectedCaseTypeForMilestones || !newMilestone.name.trim() || newMilestone.days_from_start === "") return;

    const currentTemplates = moduleSettings.cases.milestone_templates || [];
    const existingTemplateIndex = currentTemplates.findIndex(t => t.case_type === selectedCaseTypeForMilestones);

    const milestoneToAdd = {
      name: newMilestone.name.trim(),
      days_from_start: Number(newMilestone.days_from_start)
    };

    if (existingTemplateIndex !== -1) {
      const updatedTemplates = [...currentTemplates];
      const existingMilestones = updatedTemplates[existingTemplateIndex].milestones || [];
      updatedTemplates[existingTemplateIndex] = {
        ...updatedTemplates[existingTemplateIndex],
        milestones: [...existingMilestones, milestoneToAdd]
      };

      setModuleSettings(prev => ({
        ...prev,
        cases: {
          ...prev.cases,
          milestone_templates: updatedTemplates
        }
      }));
    } else {
      const newTemplate = {
        case_type: selectedCaseTypeForMilestones,
        milestones: [milestoneToAdd]
      };

      setModuleSettings(prev => ({
        ...prev,
        cases: {
          ...prev.cases,
          milestone_templates: [
            ...currentTemplates,
            newTemplate
          ]
        }
      }));
    }

    setNewMilestone({ name: "", days_from_start: "" });
  };

  const handleRemoveMilestoneTemplate = (caseType, milestoneIndex) => {
    const currentTemplates = moduleSettings.cases.milestone_templates || [];

    const updatedTemplates = currentTemplates.map(t => {
      if (t.case_type === caseType) {
        return {
          ...t,
          milestones: t.milestones.filter((_, i) => i !== milestoneIndex)
        };
      }
      return t;
    }).filter(t => t.milestones && t.milestones.length > 0);

    setModuleSettings(prev => ({
      ...prev,
      cases: {
        ...prev.cases,
        milestone_templates: updatedTemplates
      }
    }));
  };

  const getMilestonesForCaseType = (caseType) => {
    const currentTemplates = moduleSettings.cases.milestone_templates || [];
    const template = currentTemplates.find(t => t.case_type === caseType);
    return template?.milestones || [];
  };

  const handleAddServiceType = () => {
    if (!newServiceType.name.trim()) return;

    const serviceTypeToAdd = {
      id: newServiceType.name.trim().toLowerCase().replace(/\s+/g, '_'),
      name: newServiceType.name.trim(),
      pricing_type: newServiceType.pricing_type,
      default_price: Number(newServiceType.default_price) || 0,
      has_cost: newServiceType.has_cost,
      default_cost: newServiceType.has_cost ? Number(newServiceType.default_cost) || 0 : 0
    };

    setModuleSettings(prev => ({
      ...prev,
      services: {
        ...prev.services,
        custom_service_types: [...prev.services.custom_service_types, serviceTypeToAdd]
      }
    }));

    setNewServiceType({
      id: "",
      name: "",
      pricing_type: "flat",
      default_price: "",
      has_cost: false,
      default_cost: ""
    });
    setShowAddServiceType(false);
  };

  const handleEditServiceType = (serviceType) => {
    setNewServiceType({
      id: serviceType.id,
      name: serviceType.name,
      pricing_type: serviceType.pricing_type,
      default_price: serviceType.default_price,
      has_cost: serviceType.has_cost || false,
      default_cost: serviceType.default_cost || ""
    });
    setEditingServiceType(serviceType.id);
    setShowAddServiceType(true);
  };

  const handleUpdateServiceType = () => {
    if (!newServiceType.name.trim()) return;

    setModuleSettings(prev => ({
      ...prev,
      services: {
        ...prev.services,
        custom_service_types: prev.services.custom_service_types.map(st =>
          st.id === editingServiceType ? {
            ...st,
            name: newServiceType.name.trim(),
            pricing_type: newServiceType.pricing_type,
            default_price: Number(newServiceType.default_price) || 0,
            has_cost: newServiceType.has_cost,
            default_cost: newServiceType.has_cost ? Number(newServiceType.default_cost) || 0 : 0
          } : st
        )
      }
    }));

    setNewServiceType({
      id: "",
      name: "",
      pricing_type: "flat",
      default_price: "",
      has_cost: false,
      default_cost: ""
    });
    setEditingServiceType(null);
    setShowAddServiceType(false);
  };

  const handleRemoveServiceType = (typeId) => {
    setModuleSettings(prev => ({
      ...prev,
      services: {
        ...prev.services,
        custom_service_types: prev.services.custom_service_types.filter(t => t.id !== typeId)
      }
    }));
  };

  const handleCancelServiceType = () => {
    setNewServiceType({
      id: "",
      name: "",
      pricing_type: "flat",
      default_price: "",
      has_cost: false,
      default_cost: ""
    });
    setEditingServiceType(null);
    setShowAddServiceType(false);
  };

  const handleAddServiceStatus = () => {
    if (!newServiceStatus.trim()) return;

    const normalizedNewStatus = newServiceStatus.trim().toLowerCase().replace(/\s+/g, '_');
    const currentStatuses = moduleSettings.services.custom_service_statuses || [];

    if (!currentStatuses.includes(normalizedNewStatus)) {
      setModuleSettings(prev => ({
        ...prev,
        services: {
          ...prev.services,
          custom_service_statuses: [...currentStatuses, normalizedNewStatus]
        }
      }));
    }
    setNewServiceStatus("");
  };

  const handleRemoveServiceStatus = (statusToRemove) => {
    setModuleSettings(prev => ({
      ...prev,
      services: {
        ...prev.services,
        custom_service_statuses: prev.services.custom_service_statuses.filter(s => s !== statusToRemove)
      }
    }));
  };

  const handleEditServiceStatus = (oldStatus, newStatusValue) => {
    const trimmedNewStatusValue = newStatusValue.trim();
    if (!trimmedNewStatusValue) {
      handleRemoveServiceStatus(oldStatus);
      setEditingServiceStatus(null);
      return;
    }

    const normalizedNewStatus = trimmedNewStatusValue.toLowerCase().replace(/\s+/g, '_');
    const currentStatuses = moduleSettings.services.custom_service_statuses || [];

    if (normalizedNewStatus === oldStatus || (currentStatuses.includes(normalizedNewStatus) && normalizedNewStatus !== oldStatus)) {
      setEditingServiceStatus(null);
      return;
    }

    setModuleSettings(prev => ({
      ...prev,
      services: {
        ...prev.services,
        custom_service_statuses: prev.services.custom_service_statuses.map(s => s === oldStatus ? normalizedNewStatus : s)
      }
    }));
    setEditingServiceStatus(null);
  };

  const handleToggleStatusRequiresReason = (status) => {
    setModuleSettings(prev => {
      const currentRequiring = prev.contacts.statuses_requiring_reason || [];
      const currentReasons = prev.contacts.status_reasons || {};

      const isCurrentlyRequired = currentRequiring.includes(status);

      if (isCurrentlyRequired) {
        // Remove from requiring list and remove reasons
        const updatedReasons = { ...currentReasons };
        delete updatedReasons[status];

        return {
          ...prev,
          contacts: {
            ...prev.contacts,
            statuses_requiring_reason: currentRequiring.filter(s => s !== status),
            status_reasons: updatedReasons
          }
        };
      } else {
        // Add to requiring list with empty reasons array
        return {
          ...prev,
          contacts: {
            ...prev.contacts,
            statuses_requiring_reason: [...currentRequiring, status],
            status_reasons: {
              ...currentReasons,
              [status]: []
            }
          }
        };
      }
    });
  };

  const handleAddReason = (status) => {
    if (!newReason.trim()) return;

    setModuleSettings(prev => {
      const currentReasons = prev.contacts.status_reasons || {};
      const statusReasons = currentReasons[status] || [];

      if (statusReasons.includes(newReason.trim())) {
        return prev;
      }

      return {
        ...prev,
        contacts: {
          ...prev.contacts,
          status_reasons: {
            ...currentReasons,
            [status]: [...statusReasons, newReason.trim()]
          }
        }
      };
    });

    setNewReason("");
  };

  const handleRemoveReason = (status, reasonToRemove) => {
    setModuleSettings(prev => {
      const currentReasons = prev.contacts.status_reasons || {};
      const statusReasons = currentReasons[status] || [];

      return {
        ...prev,
        contacts: {
          ...prev.contacts,
          status_reasons: {
            ...currentReasons,
            [status]: statusReasons.filter(r => r !== reasonToRemove)
          }
        }
      };
    });
  };

  // Lead Sources Functions
  const handleAddLeadSource = () => {
    if (!newLeadSource.trim()) return;
    
    const newSource = {
      id: newLeadSource.toLowerCase().replace(/\s+/g, '_'),
      name: newLeadSource.trim()
    };
    
    // Check for duplicates before adding
    if (leadSourcesConfig.some(source => source.id === newSource.id)) {
      alert("Lead source with this name already exists.");
      return;
    }

    setLeadSourcesConfig([...leadSourcesConfig, newSource]);
    setNewLeadSource("");
  };

  const handleEditLeadSource = (source) => {
    setEditingLeadSourceId(source.id);
    setEditingLeadSourceName(source.name);
  };

  const handleSaveLeadSourceEdit = () => {
    if (!editingLeadSourceName.trim()) {
      alert("Lead source name cannot be empty.");
      return;
    }

    const normalizedNewNameId = editingLeadSourceName.trim().toLowerCase().replace(/\s+/g, '_');
    // Check if the new name would create a duplicate ID with another source (excluding the one being edited)
    if (leadSourcesConfig.some(source => source.id === normalizedNewNameId && source.id !== editingLeadSourceId)) {
      alert("Another lead source with this name (or similar ID) already exists.");
      return;
    }

    setLeadSourcesConfig(leadSourcesConfig.map(source =>
      source.id === editingLeadSourceId
        ? { ...source, name: editingLeadSourceName.trim(), id: normalizedNewNameId } // Update ID too if name changed
        : source
    ));
    setEditingLeadSourceId(null);
    setEditingLeadSourceName("");
  };

  const handleCancelLeadSourceEdit = () => {
    setEditingLeadSourceId(null);
    setEditingLeadSourceName("");
  };

  const handleRemoveLeadSource = (sourceId) => {
    setLeadSourcesConfig(leadSourcesConfig.filter(source => source.id !== sourceId));
  };

  const handleSaveLeadSources = async () => {
    setIsSaving(true);
    try {
      const updatedSettings = {
        ...moduleSettings,
        leads: {
          ...moduleSettings.leads,
          custom_lead_sources: leadSourcesConfig
        }
      };
      
      await User.updateMyUserData({ module_settings: updatedSettings });
      setModuleSettings(updatedSettings); // Update the main state
      alert("Lead sources saved successfully!");
    } catch (error) {
      console.error("Error saving lead sources:", error);
      alert("Error saving lead sources");
    }
    setIsSaving(false);
  };

  // Lead Assignment Functions
  const handleTogglePosition = (position) => {
    setSelectedPositions(prev => {
      const newPositions = prev.includes(position)
        ? prev.filter(p => p !== position)
        : [...prev, position];
      
      setModuleSettings(prevSettings => ({
        ...prevSettings,
        leads: {
          ...prevSettings.leads,
          assignment_positions: newPositions
        }
      }));
      
      return newPositions;
    });
  };

  const handleToggleUser = (userEmail) => {
    setSelectedUsers(prev => {
      const newUsers = prev.includes(userEmail)
        ? prev.filter(u => u !== userEmail)
        : [...prev, userEmail];
      
      setModuleSettings(prevSettings => ({
        ...prevSettings,
        leads: {
          ...prevSettings.leads,
          assignment_users: newUsers
        }
      }));
      
      return newUsers;
    });
  };

  const handleAssignmentMethodChange = (method) => {
    setAssignmentMethod(method);
    setModuleSettings(prev => ({
      ...prev,
      leads: {
        ...prev.leads,
        assignment_method: method
      }
    }));
  };

  if (isLoading) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate(createPageUrl("Settings"))}
          className="mb-6 text-slate-600 flex items-center gap-2 hover:scale-105 transition-transform duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Module Settings & Customization</h1>
          <p className="text-slate-600">Configure and customize each module in your system</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          <Tabs defaultValue="leads" className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-8">
              {modules.map((module) => (
                <TabsTrigger key={module.id} value={module.id} className="flex items-center gap-2">
                  <module.icon className="w-4 h-4" />
                  {module.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {modules.map((module) => (
              <TabsContent key={module.id} value={module.id} className="space-y-6">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 ${module.bgColor} rounded-lg`}>
                          <module.icon className={`w-5 h-5 ${module.color}`} />
                        </div>
                        <div>
                          <CardTitle className="text-xl">{module.name} Module</CardTitle>
                          <CardDescription>{module.description}</CardDescription>
                        </div>
                      </div>
                      <Badge className={moduleSettings[module.id]?.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}>
                        {moduleSettings[module.id]?.enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-base font-semibold">Enable {module.name} Module</Label>
                        <p className="text-sm text-slate-600 mt-1">Turn this module on or off for your organization</p>
                      </div>
                      <Switch
                        checked={moduleSettings[module.id]?.enabled || false}
                        onCheckedChange={(checked) => handleModuleToggle(module.id, 'enabled', checked)}
                      />
                    </div>

                    {module.id === "leads" && (
                      <>
                        {/* Auto-Assignment Section */}
                        <Card className="border-slate-200 shadow-sm">
                          <CardHeader className="border-b border-slate-100">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <Users className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <CardTitle className="text-xl">Lead Assignment Rules</CardTitle>
                                <CardDescription>Configure how new leads are automatically assigned using round-robin</CardDescription>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="p-6 space-y-6">
                            {/* Enable Auto-Assignment */}
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                              <div>
                                <Label className="text-base font-semibold">Enable Auto-Assignment</Label>
                                <p className="text-sm text-slate-600 mt-1">Automatically assign new leads to eligible users using a round-robin approach</p>
                              </div>
                              <Switch
                                checked={moduleSettings.leads.auto_assignment || false}
                                onCheckedChange={(checked) => handleModuleToggle('leads', 'auto_assignment', checked)}
                              />
                            </div>

                            {moduleSettings.leads.auto_assignment && (
                              <>
                                <Separator />

                                {/* Assignment Method */}
                                <div className="space-y-4">
                                  <Label className="text-base font-semibold">Assignment Method</Label>
                                  <div className="grid grid-cols-2 gap-4">
                                    <button
                                      type="button"
                                      onClick={() => handleAssignmentMethodChange("position")}
                                      className={`p-4 border-2 rounded-lg transition-all ${
                                        assignmentMethod === "position"
                                          ? "border-blue-500 bg-blue-50"
                                          : "border-slate-200 hover:border-slate-300"
                                      }`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                          assignmentMethod === "position" ? "border-blue-500" : "border-slate-300"
                                        }`}>
                                          {assignmentMethod === "position" && (
                                            <div className="w-3 h-3 rounded-full bg-blue-500" />
                                          )}
                                        </div>
                                        <div className="text-left">
                                          <p className="font-semibold text-slate-900">By Position</p>
                                          <p className="text-xs text-slate-600 mt-1">Assign to users with specific positions</p>
                                        </div>
                                      </div>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleAssignmentMethodChange("users")}
                                      className={`p-4 border-2 rounded-lg transition-all ${
                                        assignmentMethod === "users"
                                          ? "border-blue-500 bg-blue-50"
                                          : "border-slate-200 hover:border-slate-300"
                                      }`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                          assignmentMethod === "users" ? "border-blue-500" : "border-slate-300"
                                        }`}>
                                          {assignmentMethod === "users" && (
                                            <div className="w-3 h-3 rounded-full bg-blue-500" />
                                          )}
                                        </div>
                                        <div className="text-left">
                                          <p className="font-semibold text-slate-900">By Users</p>
                                          <p className="text-xs text-slate-600 mt-1">Assign to specific selected users</p>
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                </div>

                                <Separator />

                                {/* Position Selection */}
                                {assignmentMethod === "position" && (
                                  <div className="space-y-4">
                                    <Label className="text-base font-semibold">Select Positions</Label>
                                    <p className="text-sm text-slate-600">New leads will be assigned to users holding one of these positions in a round-robin order.</p>
                                    <div className="grid md:grid-cols-2 gap-3">
                                      {availablePositions.map((position) => (
                                        <div
                                          key={position}
                                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                                        >
                                          <Label htmlFor={`position-${position}`} className="text-sm font-medium text-slate-700 cursor-pointer flex-1">
                                            {position}
                                          </Label>
                                          <Checkbox
                                            id={`position-${position}`}
                                            checked={selectedPositions.includes(position)}
                                            onCheckedChange={() => handleTogglePosition(position)}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                    {selectedPositions.length > 0 && (
                                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <p className="text-sm text-blue-800">
                                          <span className="font-semibold">{selectedPositions.length}</span> position(s) selected. 
                                          Currently, {allUsers.filter(u => selectedPositions.includes(u.position)).length} user(s) match these positions.
                                        </p>
                                      </div>
                                    )}
                                    {selectedPositions.length === 0 && (
                                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <p className="text-sm text-yellow-800">
                                          No positions selected. Leads will not be assigned if auto-assignment is enabled with this method.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* User Selection */}
                                {assignmentMethod === "users" && (
                                  <div className="space-y-4">
                                    <Label className="text-base font-semibold">Select Users</Label>
                                    <p className="text-sm text-slate-600">New leads will be assigned to these specific users in a round-robin order.</p>
                                    <div className="space-y-2 max-h-96 overflow-y-auto border border-slate-200 rounded-lg p-4">
                                      {allUsers.map((user) => (
                                        <div
                                          key={user.email}
                                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                        >
                                          <div className="flex items-center gap-3 flex-1">
                                            <div className={`w-10 h-10 bg-gradient-to-br ${getAvatarColor(user.first_name || user.full_name)} rounded-full flex items-center justify-center shadow-sm`}>
                                              <span className="text-white font-bold text-sm">
                                                {user.first_name?.charAt(0) || user.full_name?.charAt(0) || 'U'}
                                              </span>
                                            </div>
                                            <div>
                                              <p className="font-medium text-slate-900">
                                                {user.first_name && user.last_name 
                                                  ? `${user.first_name} ${user.last_name}`
                                                  : user.full_name || user.email}
                                              </p>
                                              <p className="text-xs text-slate-600">{user.position || user.email}</p>
                                            </div>
                                          </div>
                                          <Checkbox
                                            checked={selectedUsers.includes(user.email)}
                                            onCheckedChange={() => handleToggleUser(user.email)}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                    {selectedUsers.length > 0 && (
                                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <p className="text-sm text-blue-800">
                                          <span className="font-semibold">{selectedUsers.length}</span> user(s) selected for round-robin assignment.
                                        </p>
                                      </div>
                                    )}
                                    {selectedUsers.length === 0 && (
                                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <p className="text-sm text-yellow-800">
                                          No users selected. Leads will not be assigned if auto-assignment is enabled with this method.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </CardContent>
                        </Card>

                        {/* Existing Leads Settings */}
                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Lead Statuses</Label>
                          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                            <div className="flex flex-wrap gap-2">
                              {(moduleSettings.leads.custom_statuses || []).map((status) => (
                                <div key={status} className="flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-3 pr-1 py-1">
                                  {editingStatus === status ? (
                                    <Input
                                      autoFocus
                                      defaultValue={status.replace(/_/g, ' ')}
                                      onBlur={(e) => handleEditStatus(status, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.currentTarget.blur();
                                        }
                                        if (e.key === 'Escape') {
                                          setEditingStatus(null);
                                        }
                                      }}
                                      className="h-6 w-24 px-2 text-xs border-blue-300"
                                    />
                                  ) : (
                                    <span
                                      className="text-sm text-slate-700 cursor-pointer"
                                      onClick={() => setEditingStatus(status)}
                                    >
                                      {status.replace(/_/g, ' ')}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveStatus(status)}
                                    className="text-slate-500 hover:text-red-700 p-0.5 rounded-full hover:bg-slate-100 transition-colors"
                                    aria-label={`Remove status ${status.replace(/_/g, ' ')}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add new status..."
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddStatus();
                                  }
                                }}
                                className="flex-1 border-slate-300"
                              />
                              <Button
                                type="button"
                                onClick={handleAddStatus}
                                variant="outline"
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              >
                                <Save className="w-4 h-4 mr-2" />
                                Add
                              </Button>
                            </div>
                            <p className="text-sm text-slate-600">Click on a status to edit, or click the <X className="inline-block w-3 h-3 align-text-bottom" /> icon to remove</p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Campos Obrigatórios</Label>
                          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                            <div className="grid md:grid-cols-2 gap-2">
                              {availableLeadFields.map((field) => (
                                <div
                                  key={field}
                                  className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg"
                                >
                                  <Label htmlFor={`required-field-${field}`} className="text-sm font-normal text-slate-700 cursor-pointer flex-1">
                                    {field.replace(/_/g, ' ')}
                                  </Label>
                                  <Switch
                                    id={`required-field-${field}`}
                                    checked={moduleSettings.leads.required_fields?.includes(field) || false}
                                    onCheckedChange={() => handleToggleRequiredField(field)}
                                  />
                                </div>
                              ))}
                            </div>
                            <p className="text-sm text-slate-600">Toggle fields that must be filled when creating/editing leads</p>
                          </div>
                        </div>

                        <Card className="border-slate-200 shadow-sm">
                          <CardHeader className="border-b border-slate-100">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-purple-100 rounded-lg">
                                <Users className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <CardTitle className="text-xl">Origens de Lead</CardTitle>
                                <CardDescription>Gerencie as origens de lead disponíveis</CardDescription>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="p-6 space-y-6">
                            {/* Add New Source */}
                            <div className="flex gap-2">
                              <Input
                                placeholder="New lead source name..."
                                value={newLeadSource}
                                onChange={(e) => setNewLeadSource(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddLeadSource()}
                                className="border-slate-300"
                              />
                              <Button
                                type="button"
                                onClick={handleAddLeadSource}
                                disabled={!newLeadSource.trim()}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Add
                              </Button>
                            </div>

                            {/* Sources List */}
                            <div className="space-y-2">
                              {leadSourcesConfig.map((source) => (
                                <div
                                  key={source.id}
                                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                                >
                                  {editingLeadSourceId === source.id ? (
                                    <>
                                      <Input
                                        value={editingLeadSourceName}
                                        onChange={(e) => setEditingLeadSourceName(e.target.value)}
                                        onBlur={handleSaveLeadSourceEdit} // Save on blur
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.currentTarget.blur(); // Trigger blur to save
                                          }
                                          if (e.key === 'Escape') {
                                            handleCancelLeadSourceEdit();
                                          }
                                        }}
                                        className="flex-1 mr-2 border-slate-300"
                                      />
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          onClick={handleSaveLeadSourceEdit}
                                          className="bg-emerald-600 hover:bg-emerald-700"
                                        >
                                          <Save className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={handleCancelLeadSourceEdit}
                                          className="border-slate-300"
                                        >
                                          <X className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <span className="font-medium text-slate-900">{source.name}</span>
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleEditLeadSource(source)}
                                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        >
                                          <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleRemoveLeadSource(source.id)}
                                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Save Button */}
                            <div className="flex justify-end pt-4 border-t border-slate-200">
                              <Button
                                type="button"
                                onClick={handleSaveLeadSources}
                                disabled={isSaving}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                <Save className="w-4 h-4 mr-2" />
                                {isSaving ? 'Salvando...' : 'Salvar Origens'}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </>
                    )}

                    {module.id === "deals" && (
                      <div className="space-y-8">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-4">Campos Obrigatórios</h3>
                          <p className="text-sm text-slate-500 mb-4">Selecione quais campos são obrigatórios ao criar um negócio</p>
                          <div className="grid md:grid-cols-2 gap-4">
                            {dealFields.map((field) => (
                              <div key={field.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <Label htmlFor={`deal-required-field-${field.id}`} className="text-sm font-medium text-slate-700 cursor-pointer">
                                  {field.label}
                                  {(field.id === "deal_name" || field.id === "contact_id" || field.id === "deal_type" || field.id === "stage") && (
                                    <span className="text-xs text-slate-400 ml-2">(Always required)</span>
                                  )}
                                </Label>
                                <Switch
                                  id={`deal-required-field-${field.id}`}
                                  checked={moduleSettings.deals.required_fields.includes(field.id)}
                                  onCheckedChange={(checked) => {
                                    setModuleSettings(prev => ({
                                      ...prev,
                                      deals: {
                                        ...prev.deals,
                                        required_fields: checked
                                          ? [...prev.deals.required_fields, field.id]
                                          : prev.deals.required_fields.filter(f => f !== field.id)
                                      }
                                    }));
                                  }}
                                  disabled={field.id === "deal_name" || field.id === "contact_id" || field.id === "deal_type" || field.id === "stage"}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        <Separator />

                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-4">Deal Stages</h3>
                          <p className="text-sm text-slate-500 mb-4">Customize the stages in your sales pipeline</p>
                          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                            <div className="flex flex-wrap gap-2">
                              {moduleSettings.deals.custom_stages?.map((stage, index) => (
                                <Badge key={index} variant="outline" className="capitalize text-slate-700 bg-white border-slate-200">
                                  {stage.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                            </div>
                            <p className="text-xs text-slate-600 mt-4">These are the default stages and cannot be customized at this time.</p>
                          </div>
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <Label className="text-base font-semibold text-slate-900">Auto-Assignment</Label>
                            <p className="text-sm text-slate-500 mt-1">Automatically assign new deals based on rules</p>
                          </div>
                          <Switch
                            checked={moduleSettings.deals.auto_assignment || false}
                            onCheckedChange={(checked) => handleModuleToggle('deals', 'auto_assignment', checked)}
                          />
                        </div>
                      </div>
                    )}

                    {module.id === "contacts" && ( // Changed from customers
                      <>
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <Label className="text-base font-semibold">Auto-Create from Lead</Label>
                            <p className="text-sm text-slate-600 mt-1">Automatically create contact when lead is converted</p> {/* Changed from customer */}
                          </div>
                          <Switch
                            checked={moduleSettings.contacts.auto_create_from_lead || false} // Changed from customers
                            onCheckedChange={(checked) => handleModuleToggle('contacts', 'auto_create_from_lead', checked)} // Changed from customers
                          />
                        </div>

                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Contact Statuses</Label> {/* Changed from Customer */}
                          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                            <div className="flex flex-wrap gap-2">
                              {(moduleSettings.contacts.custom_statuses || []).map((status) => ( // Changed from customers
                                <div key={status} className="flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-3 pr-1 py-1">
                                  {editingContactStatus === status ? ( // Changed from editingCustomerStatus
                                    <Input
                                      autoFocus
                                      defaultValue={status.replace(/_/g, ' ')}
                                      onBlur={(e) => handleEditContactStatus(status, e.target.value)} // Changed from handleEditCustomerStatus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.currentTarget.blur();
                                        }
                                        if (e.key === 'Escape') {
                                          setEditingContactStatus(null); // Changed from setEditingCustomerStatus
                                        }
                                      }}
                                      className="h-6 w-24 px-2 text-xs border-blue-300"
                                    />
                                  ) : (
                                    <span
                                      className="text-sm text-slate-700 cursor-pointer"
                                      onClick={() => setEditingContactStatus(status)} // Changed from setEditingCustomerStatus
                                    >
                                      {status.replace(/_/g, ' ')}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveContactStatus(status)} // Changed from handleRemoveCustomerStatus
                                    className="text-slate-500 hover:text-red-700 p-0.5 rounded-full hover:bg-slate-100 transition-colors"
                                    aria-label={`Remove status ${status.replace(/_/g, ' ')}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add new status..."
                                value={newContactStatus} // Changed from newCustomerStatus
                                onChange={(e) => setNewContactStatus(e.target.value)} // Changed from setNewCustomerStatus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddContactStatus(); // Changed from handleAddCustomerStatus
                                  }
                                }}
                                className="flex-1 border-slate-300"
                              />
                              <Button
                                type="button"
                                onClick={handleAddContactStatus} // Changed from handleAddCustomerStatus
                                variant="outline"
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              >
                                <Save className="w-4 h-4 mr-2" />
                                Add
                              </Button>
                            </div>
                            <p className="text-sm text-slate-600">Click on a status to edit, or click the <X className="inline-block w-3 h-3 align-text-bottom" /> icon to remove</p>
                          </div>
                        </div>

                        <Separator />

                        <div className="space-y-4">
                          <div>
                            <Label className="text-base font-semibold">Status Reasons Configuration</Label>
                            <p className="text-sm text-slate-600 mt-1">Configure which statuses require a reason and what the available reasons are</p>
                          </div>

                          <div className="space-y-4">
                            {(moduleSettings.contacts.custom_statuses || []).map((status) => {
                              const requiresReason = (moduleSettings.contacts.statuses_requiring_reason || []).includes(status);
                              const reasons = (moduleSettings.contacts.status_reasons || {})[status] || [];

                              return (
                                <Card key={status} className="border-slate-200">
                                  <CardContent className="p-4">
                                    <div className="space-y-4">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <Label className="text-base font-medium capitalize">{status.replace(/_/g, ' ')}</Label>
                                          <p className="text-xs text-slate-500 mt-1">Require reason when contact is set to this status</p>
                                        </div>
                                        <Switch
                                          checked={requiresReason}
                                          onCheckedChange={() => handleToggleStatusRequiresReason(status)}
                                        />
                                      </div>

                                      {requiresReason && (
                                        <div className="pl-4 border-l-2 border-blue-200 space-y-3">
                                          <Label className="text-sm font-medium">Available Reasons</Label>

                                          {reasons.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                              {reasons.map((reason, index) => (
                                                <Badge
                                                  key={index}
                                                  variant="outline"
                                                  className="px-3 py-1.5 bg-white"
                                                >
                                                  {reason}
                                                  <button
                                                    type="button"
                                                    onClick={() => handleRemoveReason(status, reason)}
                                                    className="ml-2 text-slate-500 hover:text-red-600"
                                                  >
                                                    <X className="w-3 h-3" />
                                                  </button>
                                                </Badge>
                                              ))}
                                            </div>
                                          )}

                                          <div className="flex gap-2">
                                            <Input
                                              placeholder={`Add reason for ${status.replace(/_/g, ' ')}...`}
                                              value={selectedStatusForReasons === status ? newReason : ""}
                                              onFocus={() => setSelectedStatusForReasons(status)}
                                              onChange={(e) => setNewReason(e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  e.preventDefault();
                                                  handleAddReason(status);
                                                }
                                              }}
                                              className="flex-1 border-slate-300"
                                            />
                                            <Button
                                              type="button"
                                              onClick={() => handleAddReason(status)}
                                              variant="outline"
                                              size="sm"
                                              className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                            >
                                              <Plus className="w-4 h-4 mr-2" />
                                              Add
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}

                    {module.id === "cases" && (
                      <div className="space-y-8">
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <Label className="text-base font-semibold">Auto Notifications</Label>
                            <p className="text-sm text-slate-600 mt-1">Send automatic notifications for case updates</p>
                          </div>
                          <Switch
                            checked={moduleSettings.cases.auto_notifications || false}
                            onCheckedChange={(checked) => handleModuleToggle('cases', 'auto_notifications', checked)}
                          />
                        </div>

                        <Card className="border-slate-200 shadow-sm">
                          <CardHeader className="border-b border-slate-100">
                            <CardTitle className="text-lg">Custom Case Types</CardTitle>
                            <CardDescription>
                              Add and configure case types for your practice
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="p-6 space-y-6">
                            {(moduleSettings.cases.custom_case_types || []).length > 0 && (
                              <div className="space-y-3">
                                {(moduleSettings.cases.custom_case_types || []).map((caseType) => {
                                  const caseTypeObj = typeof caseType === 'string'
                                    ? { id: caseType, name: caseType.replace(/_/g, ' '), pricing_type: "flat", default_price: 0, has_cost: false, default_cost: 0 }
                                    : caseType;
                                  const labels = moduleSettings.cases.case_person_labels?.[caseTypeObj.id] || {
                                    primary_person_label: "Applicant",
                                    show_dependents: false,
                                    dependent_label: "",
                                    can_differ_from_customer: false,
                                    show_secondary_person: false,
                                    secondary_person_label: "",
                                    secondary_person_can_differ_from_customer: false
                                  };

                                  return (
                                    <div key={caseTypeObj.id} className={`border rounded-lg p-4 border-slate-200 hover:border-slate-300 transition-colors`}>
                                      <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1">
                                          <h4 className="font-semibold text-slate-900 mb-2">
                                            {caseTypeObj.name.replace(/\b\w/g, l => l.toUpperCase())}
                                          </h4>

                                          {/* Pricing Information */}
                                          <div className="grid md:grid-cols-3 gap-4 text-sm mb-3">
                                            <div>
                                              <p className="text-xs text-slate-500 mb-1">Pricing Type</p>
                                              <Badge variant="outline" className={caseTypeObj.pricing_type === 'flat' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}>
                                                {caseTypeObj.pricing_type === 'flat' ? 'Flat Rate' : 'Variable'}
                                              </Badge>
                                            </div>
                                            <div>
                                              <p className="text-xs text-slate-500 mb-1">Default Price</p>
                                              <p className="font-medium text-slate-900">${Number(caseTypeObj.default_price || 0).toLocaleString()}</p>
                                            </div>
                                            {caseTypeObj.has_cost && (
                                              <div>
                                                <p className="text-xs text-slate-500 mb-1">Default Cost</p>
                                                <p className="font-medium text-slate-900">${Number(caseTypeObj.default_cost || 0).toLocaleString()}</p>
                                              </div>
                                            )}
                                          </div>

                                          {/* Person Labels */}
                                          <div className="flex flex-wrap gap-2 text-sm text-slate-600">
                                            <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                              {labels.primary_person_label}
                                            </span>
                                            {labels.show_secondary_person && (
                                              <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded">
                                                Includes {labels.secondary_person_label}
                                              </span>
                                            )}
                                            {labels.show_dependents && (
                                              <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded">
                                                Includes {labels.dependent_label}s
                                              </span>
                                            )}
                                            {labels.can_differ_from_customer && (
                                              <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded">
                                                Primary Can differ from contact
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex gap-2">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleEditCaseType(caseTypeObj)}
                                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                          >
                                            <Edit className="w-4 h-4" />
                                          </Button>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteCaseType(caseTypeObj)}
                                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            <Button
                              type="button"
                              onClick={() => {
                                handleCancelCaseType();
                                setCaseTypeModalOpen(true);
                              }}
                              variant="outline"
                              className="w-full border-dashed border-2 border-slate-300 hover:border-blue-500 hover:bg-blue-50"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add Case Type
                            </Button>
                          </CardContent>
                        </Card>

                        {/* Case Type Modal */}
                        <Dialog open={caseTypeModalOpen} onOpenChange={setCaseTypeModalOpen}>
                          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>
                                {editingCaseType ? `Edit: ${editingCaseType.name}` : 'New Case Type'}
                              </DialogTitle>
                              <DialogDescription>
                                Configure the case type details, pricing, and person labels
                              </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-6 py-4">
                              <div className="space-y-2">
                                <Label className="text-slate-700 font-medium">
                                  Case Type Name <span className="text-red-500">*</span>
                                  {editingCaseType && <span className="text-xs text-slate-500 ml-2">(Cannot be changed)</span>}
                                </Label>
                                <Input
                                  value={newCaseType.name}
                                  onChange={(e) => setNewCaseType({...newCaseType, name: e.target.value})}
                                  placeholder="e.g., Asylum, Employment Based, Family Based"
                                  className="border-slate-300"
                                  disabled={!!editingCaseType}
                                />
                                {!editingCaseType && (
                                  <p className="text-xs text-slate-500">
                                    This will be converted to: {newCaseType.name.trim() ? newCaseType.name.trim().toLowerCase().replace(/\s+/g, '_') : 'type_name'}
                                  </p>
                                )}
                              </div>

                              <Separator />

                              <div className="space-y-4">
                                <h5 className="font-medium text-slate-900">Pricing Configuration</h5>

                                <div className="space-y-2">
                                  <Label>Pricing Type</Label>
                                  <Select
                                    value={newCaseType.pricing_type}
                                    onValueChange={(value) => setNewCaseType({...newCaseType, pricing_type: value})}
                                  >
                                    <SelectTrigger className="border-slate-300">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="flat">Flat Rate (Fixed Price)</SelectItem>
                                      <SelectItem value="variable">Variable (Price × Quantity)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <p className="text-xs text-slate-500">
                                    {newCaseType.pricing_type === 'flat'
                                      ? 'One fixed price for this case type'
                                      : 'Price can vary based on case complexity or quantity'}
                                  </p>
                                </div>

                                <div className="space-y-2">
                                  <Label>Default Price (USD)</Label>
                                  <Input
                                    type="number"
                                    value={newCaseType.default_price}
                                    onChange={(e) => setNewCaseType({...newCaseType, default_price: e.target.value})}
                                    placeholder="0.00"
                                    step="0.01"
                                    className="border-slate-300"
                                  />
                                </div>

                                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                  <div>
                                    <Label className="font-medium">Track Cost</Label>
                                    <p className="text-xs text-slate-500 mt-1">Track internal costs for profit margin calculation</p>
                                  </div>
                                  <Switch
                                    checked={newCaseType.has_cost}
                                    onCheckedChange={(checked) => setNewCaseType({...newCaseType, has_cost: checked, default_cost: checked ? newCaseType.default_cost : ""})}
                                  />
                                </div>

                                {newCaseType.has_cost && (
                                  <div className="space-y-2">
                                    <Label>Default Cost (USD)</Label>
                                    <Input
                                      type="number"
                                      value={newCaseType.default_cost}
                                      onChange={(e) => setNewCaseType({...newCaseType, default_cost: e.target.value})}
                                      placeholder="0.00"
                                      step="0.01"
                                      className="border-slate-300"
                                    />
                                    <p className="text-xs text-slate-500">Your internal cost for handling this case type</p>
                                  </div>
                                )}
                              </div>

                              <Separator />

                              <div className="space-y-4">
                                <h5 className="font-medium text-slate-900">Primary Person Configuration</h5>

                                <div className="space-y-2">
                                  <Label className="text-slate-700 font-medium">Primary Person Label <span className="text-red-500">*</span></Label>
                                  <Select
                                    value={newCaseTypeLabels.primary_person_label}
                                    onValueChange={(value) => setNewCaseTypeLabels(prev => ({
                                      ...prev,
                                      primary_person_label: value
                                    }))}
                                  >
                                    <SelectTrigger className="border-slate-300">
                                      <SelectValue placeholder="Select primary person label" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Applicant">Applicant</SelectItem>
                                      <SelectItem value="Petitioner">Petitioner</SelectItem>
                                      <SelectItem value="Beneficiary">Beneficiary</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <p className="text-xs text-slate-500">
                                    What do you call the main person in this case type?
                                  </p>
                                </div>

                                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                  <div>
                                    <Label className="text-slate-700 font-medium">Can Differ from Contact</Label>
                                    <p className="text-xs text-slate-500 mt-1">
                                      Allow the primary person to be different from the contact who contracted the service
                                    </p>
                                  </div>
                                  <Switch
                                    checked={newCaseTypeLabels.can_differ_from_customer}
                                    onCheckedChange={(checked) => setNewCaseTypeLabels(prev => ({
                                      ...prev,
                                      can_differ_from_customer: checked
                                    }))}
                                  />
                                </div>

                                <Separator />

                                <div className="space-y-4">
                                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                    <div>
                                      <Label className="text-slate-700 font-medium">Show Secondary Person</Label>
                                      <p className="text-xs text-slate-500 mt-1">
                                        Include an additional person field in this case type
                                      </p>
                                    </div>
                                    <Switch
                                      checked={newCaseTypeLabels.show_secondary_person}
                                      onCheckedChange={(checked) => setNewCaseTypeLabels(prev => ({
                                        ...prev,
                                        show_secondary_person: checked,
                                        secondary_person_label: checked ? prev.secondary_person_label : "",
                                        secondary_person_can_differ_from_customer: checked ? prev.secondary_person_can_differ_from_customer : false
                                      }))}
                                    />
                                  </div>

                                  {newCaseTypeLabels.show_secondary_person && (
                                    <div className="space-y-4 pl-4 border-l-2 border-indigo-300">
                                      <div className="space-y-2">
                                        <Label className="text-slate-700 font-medium">Secondary Person Label <span className="text-red-500">*</span></Label>
                                        <Select
                                          value={newCaseTypeLabels.secondary_person_label}
                                          onValueChange={(value) => setNewCaseTypeLabels(prev => ({
                                            ...prev,
                                            secondary_person_label: value
                                          }))}
                                        >
                                          <SelectTrigger className="border-slate-300">
                                            <SelectValue placeholder="Select secondary person label" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="Applicant">Applicant</SelectItem>
                                            <SelectItem value="Petitioner">Petitioner</SelectItem>
                                            <SelectItem value="Beneficiary">Beneficiary</SelectItem>
                                          </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-500">
                                          What do you call the secondary person in this case type?
                                        </p>
                                      </div>

                                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                        <div>
                                          <Label className="text-slate-700 font-medium">Can Differ from Contact</Label>
                                          <p className="text-xs text-slate-500 mt-1">
                                            Allow the secondary person to be different from the contact
                                          </p>
                                        </div>
                                        <Switch
                                          checked={newCaseTypeLabels.secondary_person_can_differ_from_customer}
                                          onCheckedChange={(checked) => setNewCaseTypeLabels(prev => ({
                                            ...prev,
                                            secondary_person_can_differ_from_customer: checked
                                          }))}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <Separator />

                                <div className="space-y-4">
                                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                    <div>
                                      <Label className="text-slate-700 font-medium">Show Dependents</Label>
                                      <p className="text-xs text-slate-500 mt-1">
                                        Include dependent/family member fields in this case type (will be labeled as "Dependent")
                                      </p>
                                    </div>
                                    <Switch
                                      checked={newCaseTypeLabels.show_dependents}
                                      onCheckedChange={(checked) => setNewCaseTypeLabels(prev => ({
                                        ...prev,
                                        show_dependents: checked,
                                        dependent_label: checked ? "Dependent" : ""
                                      }))}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="flex justify-end gap-3 pt-4">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={handleCancelCaseType}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  onClick={() => {
                                    handleSaveCaseType();
                                    setCaseTypeModalOpen(false);
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700"
                                  disabled={
                                    !newCaseType.name.trim() ||
                                    !newCaseTypeLabels.primary_person_label.trim() ||
                                    (newCaseTypeLabels.show_secondary_person && !newCaseTypeLabels.secondary_person_label.trim())
                                  }
                                >
                                  <Save className="w-4 h-4 mr-2" />
                                  {editingCaseType ? 'Update' : 'Add'} Case Type
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>

                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Case Statuses</Label>
                          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                            <div className="flex flex-wrap gap-2">
                              {(moduleSettings.cases.custom_case_statuses || []).map((status) => (
                                <div key={status} className="flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-3 pr-1 py-1">
                                  {editingCaseStatus === status ? (
                                    <Input
                                      autoFocus
                                      defaultValue={status.replace(/_/g, ' ')}
                                      onBlur={(e) => handleEditCaseStatus(status, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.currentTarget.blur();
                                        }
                                        if (e.key === 'Escape') {
                                          setEditingCaseStatus(null);
                                        }
                                      }}
                                      className="h-6 w-24 px-2 text-xs border-blue-300"
                                    />
                                  ) : (
                                    <span
                                      className="text-sm text-slate-700 cursor-pointer"
                                      onClick={() => setEditingCaseStatus(status)}
                                    >
                                      {status.replace(/_/g, ' ')}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveCaseStatus(status)}
                                    className="text-slate-500 hover:text-red-700 p-0.5 rounded-full hover:bg-slate-100 transition-colors"
                                    aria-label={`Remove status ${status.replace(/_/g, ' ')}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add new status..."
                                value={newCaseStatus}
                                onChange={(e) => setNewCaseStatus(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddCaseStatus();
                                  }
                                }}
                                className="flex-1 border-slate-300"
                              />
                              <Button
                                type="button"
                                onClick={handleAddCaseStatus}
                                variant="outline"
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              >
                                <Save className="w-4 h-4 mr-2" />
                                Add
                              </Button>
                            </div>
                            <p className="text-sm text-slate-600">Click on a status to edit, or click the <X className="inline-block w-3 h-3 align-text-bottom" /> icon to remove</p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Milestone Templates by Case Type</Label>
                          <p className="text-sm text-slate-600">Create step-by-step milestones for each case type with automatic due dates</p>

                          <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="milestone-case-type-select">Select Case Type</Label>
                              <Select
                                value={selectedCaseTypeForMilestones}
                                onValueChange={setSelectedCaseTypeForMilestones}
                              >
                                <SelectTrigger id="milestone-case-type-select" className="border-slate-300 bg-white">
                                  <SelectValue placeholder="Select a case type to add milestones" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(moduleSettings.cases.custom_case_types || []).map((type) => (
                                    <SelectItem key={type.id} value={type.id}>
                                      {type.name.replace(/\b\w/g, l => l.toUpperCase())}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {selectedCaseTypeForMilestones && (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm font-medium">
                                    Milestones for {selectedCaseTypeForMilestones.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </Label>
                                  <Badge variant="outline" className="text-xs">
                                    {getMilestonesForCaseType(selectedCaseTypeForMilestones).length} steps
                                  </Badge>
                                </div>

                                <div className="space-y-2 bg-white rounded-lg p-3 border border-slate-200">
                                  {getMilestonesForCaseType(selectedCaseTypeForMilestones).length > 0 ? (
                                    getMilestonesForCaseType(selectedCaseTypeForMilestones).map((milestone, index) => (
                                      <div key={`${selectedCaseTypeForMilestones}-${index}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 font-semibold text-xs">
                                            {index + 1}
                                          </div>
                                          <div>
                                            <p className="font-medium text-slate-900">{milestone.name}</p>
                                            <p className="text-xs text-slate-500">Due: {milestone.days_from_start} days from case start</p>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveMilestoneTemplate(selectedCaseTypeForMilestones, index)}
                                          className="text-slate-400 hover:text-red-600 p-1 rounded hover:bg-white transition-colors"
                                          aria-label={`Remove milestone ${milestone.name}`}
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-sm text-slate-500 text-center py-4">No milestones yet. Add one below.</p>
                                  )}
                                </div>

                                <div className="space-y-2 bg-white rounded-lg p-3 border border-slate-200">
                                  <Label className="text-sm">Add New Milestone</Label>
                                  <div className="grid grid-cols-3 gap-2">
                                    <div className="col-span-2">
                                      <Input
                                        placeholder="Milestone name (e.g., Submit Form I-130)"
                                        value={newMilestone.name}
                                        onChange={(e) => setNewMilestone(prev => ({ ...prev, name: e.target.value }))}
                                        className="border-slate-300"
                                      />
                                    </div>
                                    <Input
                                      type="number"
                                      placeholder="Days"
                                      value={newMilestone.days_from_start}
                                      onChange={(e) => setNewMilestone(prev => ({ ...prev, days_from_start: e.target.value }))}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleAddMilestoneTemplate();
                                        }
                                      }}
                                      className="border-slate-300"
                                    />
                                  </div>
                                  <Button
                                    type="button"
                                    onClick={handleAddMilestoneTemplate}
                                    variant="outline"
                                    className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                                    disabled={!newMilestone.name.trim() || newMilestone.days_from_start === ""}
                                  >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add Milestone
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {module.id === "services" && (
                      <div className="space-y-8">
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <Label className="text-base font-semibold">Auto Notifications</Label>
                            <p className="text-sm text-slate-600 mt-1">Send automatic notifications for service updates</p>
                          </div>
                          <Switch
                            checked={moduleSettings.services.auto_notifications || false}
                            onCheckedChange={(checked) => handleModuleToggle('services', 'auto_notifications', checked)}
                          />
                        </div>

                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Service Types & Pricing</Label>

                          {(moduleSettings.services.custom_service_types || []).length > 0 && (
                            <div className="space-y-3">
                              {(moduleSettings.services.custom_service_types || []).map((serviceType) => (
                                <Card key={serviceType.id} className="border-slate-200">
                                  <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <h4 className="font-semibold text-slate-900 mb-2">
                                          {serviceType.name}
                                        </h4>
                                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                                          <div>
                                            <p className="text-xs text-slate-500 mb-1">Pricing Type</p>
                                            <Badge variant="outline" className={serviceType.pricing_type === 'flat' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}>
                                              {serviceType.pricing_type === 'flat' ? 'Flat Rate' : 'Variable (by quantity)'}
                                            </Badge>
                                          </div>
                                          <div>
                                            <p className="text-xs text-slate-500 mb-1">Default Price</p>
                                            <p className="font-medium text-slate-900">${Number(serviceType.default_price || 0).toLocaleString()}</p>
                                          </div>
                                          {serviceType.has_cost && (
                                            <div>
                                              <p className="text-xs text-slate-500 mb-1">Default Cost</p>
                                              <p className="font-medium text-slate-900">${Number(serviceType.default_cost || 0).toLocaleString()}</p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleEditServiceType(serviceType)}
                                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleRemoveServiceType(serviceType.id)}
                                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          )}

                          {!showAddServiceType ? (
                            <Button
                              type="button"
                              onClick={() => {
                                handleCancelServiceType(); // Ensure form is reset before opening
                                setShowAddServiceType(true);
                              }}
                              variant="outline"
                              className="w-full border-dashed border-2 border-slate-300 hover:border-blue-500 hover:bg-blue-50"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add Service Type
                            </Button>
                          ) : (
                            <Card className="border-2 border-blue-200 bg-blue-50/50">
                              <CardContent className="p-6 space-y-4">
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className="font-semibold text-slate-900">
                                    {editingServiceType ? 'Edit Service Type' : 'New Service Type'}
                                  </h4>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCancelServiceType}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>

                                <div className="space-y-2">
                                  <Label>Service Name <span className="text-red-500">*</span></Label>
                                  <Input
                                    value={newServiceType.name}
                                    onChange={(e) => setNewServiceType({...newServiceType, name: e.target.value})}
                                    placeholder="e.g., Premium Package"
                                    className="border-slate-300 bg-white"
                                    disabled={!!editingServiceType} // Disable name edit if editing an existing type
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label>Pricing Type</Label>
                                  <Select
                                    value={newServiceType.pricing_type}
                                    onValueChange={(value) => setNewServiceType({...newServiceType, pricing_type: value})}
                                  >
                                    <SelectTrigger className="border-slate-300 bg-white">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="flat">Flat Rate (Fixed Price)</SelectItem>
                                      <SelectItem value="variable">Variable (Price × Quantity)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <p className="text-xs text-slate-500">
                                    {newServiceType.pricing_type === 'flat'
                                      ? 'One fixed price regardless of quantity'
                                      : 'Price multiplied by quantity (e.g., per document, per hour)'}
                                  </p>
                                </div>

                                <div className="space-y-2">
                                  <Label>Default Price (USD)</Label>
                                  <Input
                                    type="number"
                                    value={newServiceType.default_price}
                                    onChange={(e) => setNewServiceType({...newServiceType, default_price: e.target.value})}
                                    placeholder="0.00"
                                    step="0.01"
                                    className="border-slate-300 bg-white"
                                  />
                                </div>

                                <Separator />

                                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                                  <div>
                                    <Label className="font-medium">Track Cost</Label>
                                    <p className="text-xs text-slate-500 mt-1">Track internal costs for profit margin calculation</p>
                                  </div>
                                  <Switch
                                    checked={newServiceType.has_cost}
                                    onCheckedChange={(checked) => setNewServiceType({...newServiceType, has_cost: checked})}
                                  />
                                </div>

                                {newServiceType.has_cost && (
                                  <div className="space-y-2">
                                    <Label>Default Cost (USD)</Label>
                                    <Input
                                      type="number"
                                      value={newServiceType.default_cost}
                                      onChange={(e) => setNewServiceType({...newServiceType, default_cost: e.target.value})}
                                      placeholder="0.00"
                                      step="0.01"
                                      className="border-slate-300 bg-white"
                                    />
                                    <p className="text-xs text-slate-500">Your internal cost for this service</p>
                                  </div>
                                )}

                                <div className="flex justify-end gap-3 pt-4">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancelServiceType}
                                    className="border-slate-300"
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    type="button"
                                    onClick={editingServiceType ? handleUpdateServiceType : handleAddServiceType}
                                    className="bg-blue-600 hover:bg-blue-700"
                                    disabled={!newServiceType.name.trim()}
                                  >
                                    <Save className="w-4 h-4 mr-2" />
                                    {editingServiceType ? 'Update' : 'Add'} Service Type
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>

                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Service Statuses</Label>
                          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                            <div className="flex flex-wrap gap-2">
                              {(moduleSettings.services.custom_service_statuses || []).map((status) => (
                                <div key={status} className="flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-3 pr-1 py-1">
                                  {editingServiceStatus === status ? (
                                    <Input
                                      autoFocus
                                      defaultValue={status.replace(/_/g, ' ')}
                                      onBlur={(e) => handleEditServiceStatus(status, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.currentTarget.blur();
                                        }
                                        if (e.key === 'Escape') {
                                          setEditingServiceStatus(null);
                                        }
                                      }}
                                      className="h-6 w-24 px-2 text-xs border-blue-300"
                                    />
                                  ) : (
                                    <span
                                      className="text-sm text-slate-700 cursor-pointer"
                                      onClick={() => setEditingServiceStatus(status)}
                                    >
                                      {status.replace(/_/g, ' ')}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveServiceStatus(status)}
                                    className="text-slate-500 hover:text-red-700 p-0.5 rounded-full hover:bg-slate-100 transition-colors"
                                    aria-label={`Remove status ${status.replace(/_/g, ' ')}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add new status..."
                                value={newServiceStatus}
                                onChange={(e) => setNewServiceStatus(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddServiceStatus();
                                  }
                                }}
                                className="flex-1 border-slate-300"
                              />
                              <Button
                                type="button"
                                onClick={handleAddServiceStatus}
                                variant="outline"
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              >
                                <Save className="w-4 h-4 mr-2" />
                                Add
                              </Button>
                            </div>
                            <p className="text-sm text-slate-600">Click on a status to edit, or click the <X className="inline-block w-3 h-3 align-text-bottom" /> icon to remove</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={loadData}
              disabled={isSaving}
              className="border-slate-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Module Settings'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
